import * as React from 'react';
import { ComponentProps } from 'react';
import { Bar, BarChart, CartesianGrid, Label, XAxis, YAxis } from 'recharts';
import * as RechartsPrimitive from 'recharts';

import InfoToolTip from 'DesignSystem/features/InfoTooltip';
import { ChartConfig, ChartContainer, ChartTooltip } from 'DesignSystem/ui/chart';
import type { FrequencyOfUseData } from 'pages/preference-cards/types';
import { formatNumber } from 'utils/helpers';

type FrequencyOfUseChartData = {
  count_items: number;
  count_cases: number;
  percent_cases: number;
};

const TOOLTIP_TEXT = [
  'Distribution of the number of units used per case for the selected item over the past 12 months',
];

const chartConfig = {
  percent_cases: {
    label: 'Units Used',
    color: 'hsl(var(--chart-primary))',
  },
} satisfies ChartConfig;

type FrequencyOfUseChartProps = {
  frequency: FrequencyOfUseData[];
  caseCount: number;
};

export function FrequencyOfUseChart({ frequency, caseCount }: FrequencyOfUseChartProps) {
  const subText = `${caseCount.toLocaleString()} case(s) in the last 12 months`;
  const data: FrequencyOfUseChartData[] = React.useMemo(() => {
    return frequency.map((f) => ({
      count_items: f.count_items,
      count_cases: f.count_cases,
      percent_cases: formatYAxisValue(f.count_cases, caseCount),
    }));
  }, [frequency, caseCount]);

  return (
    <div className="flex flex-col gap-0.5">
      <div className="type-body-xs flex items-center font-medium text-zinc-800">
        <InfoToolTip iconClassName="h-4 w-4" info={TOOLTIP_TEXT}>
          Frequency of Use
        </InfoToolTip>
      </div>
      <div className="type-body-2xs leading-4 text-gray-400">{subText}</div>

      <ChartContainer config={chartConfig} className="mt-1 h-full min-h-[230px] w-full">
        <BarChart data={data}>
          <CartesianGrid />
          <XAxis dataKey="count_items" tickLine={false} tickMargin={3} axisLine={false} fontSize={9}>
            <Label value="Units Used" offset={-4} position="insideBottom" />
          </XAxis>

          <YAxis
            dataKey="percent_cases"
            tickLine={false}
            tickMargin={-1}
            axisLine={false}
            fontSize={9}
            tickCount={6}
            type="number"
            width={40}
            tickFormatter={(value) => `${value}%`}
            ticks={[0, 20, 40, 60, 80, 100]}
          >
            <Label
              value="% of Cases Used"
              angle={-90}
              offset={0}
              position="insideLeft"
              style={{ textAnchor: 'middle' }}
            />
          </YAxis>

          <Bar dataKey="percent_cases" fill="var(--color-percent_cases)" radius={4} />
          <ChartTooltip cursor={false} content={<CustomTooltip />} />
        </BarChart>
      </ChartContainer>
    </div>
  );
}

function formatYAxisValue(caseCount: number, totalCases: number): number {
  const value = formatNumber((caseCount / totalCases) * 100, 1);
  return parseFloat(value);
}

const CustomTooltip = ({ active, payload }: ComponentProps<typeof RechartsPrimitive.Tooltip>) => {
  if (active && payload && payload.length) {
    const chartData = payload[0].payload as FrequencyOfUseChartData;

    return (
      <div className="type-body-2xs grid min-w-[6rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-zinc-800 shadow-xl">
        <div>
          <div className="type-body-2xs mb-1.5 font-medium">{chartData.count_items} Units Used</div>
          <div className="type-body-2xs">
            {chartData.count_cases.toLocaleString()} Cases ({chartData.percent_cases}%)
          </div>
        </div>
      </div>
    );
  }

  return null;
};
